import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { useCreateReply, usePolishReply } from '@/hooks/replies';
import { useSession } from '@/lib/auth-client';
import type { SenderType } from './types';

interface Props {
  ticketId: string;
}

export function ReplyForm({ ticketId }: Props) {
  const { data: session } = useSession();
  const role = (session?.user as { role?: string } | undefined)?.role;
  const canPolish = role === 'ADMIN' || role === 'AGENT';
  const [body, setBody] = useState('');
  const [senderType, setSenderType] = useState<SenderType>(canPolish ? 'AGENT' : 'CUSTOMER');
  const createMutation = useCreateReply(ticketId);
  const polishMutation = usePolishReply(ticketId);

  function handleSubmit() {
    createMutation.mutate(
      { body, senderType },
      { onSuccess: () => setBody('') },
    );
  }

  function handlePolish() {
    polishMutation.mutate(body, {
      onSuccess: (polishedBody) => setBody(polishedBody),
    });
  }

  return (
    <div>
      <p className='text-[10px] font-bold tracking-[0.12em] uppercase mb-3' style={{ color: 'var(--muted-foreground)' }}>
        Add reply
      </p>
      <div className='space-y-2'>
        <div className='flex gap-2'>
          {canPolish && (
            <button
              type='button'
              onClick={() => setSenderType('AGENT')}
              className='px-3 py-1 rounded text-xs font-medium border transition-all'
              style={
                senderType === 'AGENT'
                  ? { background: 'oklch(0.72 0.14 220 / 0.12)', borderColor: 'oklch(0.72 0.14 220 / 0.3)', color: 'oklch(0.72 0.14 220)' }
                  : { borderColor: 'var(--border)', color: 'var(--muted-foreground)' }
              }
            >
              Agent
            </button>
          )}
          {!canPolish && (
            <button
              type='button'
              onClick={() => setSenderType('CUSTOMER')}
              className='px-3 py-1 rounded text-xs font-medium border transition-all'
              style={
                senderType === 'CUSTOMER'
                  ? { background: 'var(--muted)', borderColor: 'var(--border)', color: 'var(--foreground)' }
                  : { borderColor: 'var(--border)', color: 'var(--muted-foreground)' }
              }
            >
              Customer
            </button>
          )}
        </div>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder='Write your reply…'
          rows={4}
          className='w-full rounded-md px-3 py-2 text-sm resize-none transition-colors focus-visible:outline-none'
          style={{
            background: 'var(--card)',
            border: '1px solid var(--border)',
            color: 'var(--foreground)',
          }}
          onFocus={(e) => {
            (e.target as HTMLTextAreaElement).style.borderColor = 'oklch(0.65 0.22 285 / 0.5)';
            (e.target as HTMLTextAreaElement).style.boxShadow = '0 0 0 2px oklch(0.65 0.22 285 / 0.15)';
          }}
          onBlur={(e) => {
            (e.target as HTMLTextAreaElement).style.borderColor = 'var(--border)';
            (e.target as HTMLTextAreaElement).style.boxShadow = 'none';
          }}
        />
        {createMutation.isError && (
          <p className='text-xs text-destructive'>Failed to send reply.</p>
        )}
        {polishMutation.isError && (
          <p className='text-xs text-destructive'>
            {axios.isAxiosError(polishMutation.error)
              ? (polishMutation.error.response?.data?.error ?? 'Failed to polish reply.')
              : 'Failed to polish reply.'}
          </p>
        )}
        <div className='flex gap-2'>
          {canPolish && (
            <Button
              variant='outline'
              size='sm'
              onClick={handlePolish}
              disabled={!body.trim() || polishMutation.isPending}
            >
              {polishMutation.isPending ? 'Polishing…' : '✨ Polish'}
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            disabled={!body.trim() || createMutation.isPending}
            size='sm'
          >
            {createMutation.isPending ? 'Sending…' : 'Send reply'}
          </Button>
        </div>
      </div>
    </div>
  );
}
